import React, { useState, useEffect } from 'react';
import { getEscrowStatistics } from '../../services/api';
import { 
  FiTrendingUp,
  FiTrendingDown,
  FiDollarSign,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiAlertTriangle,
  FiUsers,
  FiShoppingBag,
  FiMessageSquare,
  FiDownload,
  FiCalendar,
  FiRefreshCw,
  FiPieChart,
  FiBarChart2,
  FiActivity,
  FiDatabase,
  FiPercent,
  FiShield,
  FiCreditCard
} from 'react-icons/fi';

function EscrowStatistics() {
  const [stats, setStats] = useState({
    overview: {},
    status_distribution: [],
    revenue_metrics: [],
    dispute_stats: {},
    user_metrics: {},
    type_distribution: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [period, setPeriod] = useState('month');
  const [dateRange, setDateRange] = useState({
    date_from: '',
    date_to: ''
  });

  useEffect(() => {
    fetchStatistics();
  }, [period, dateRange]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');
      
      const params = { period };
      if (dateRange.date_from) params.date_from = dateRange.date_from;
      if (dateRange.date_to) params.date_to = dateRange.date_to;

      const response = await getEscrowStatistics(params);
      if (response.success) {
        setStats(response.data);
        setSuccessMessage('Statistics updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.message || 'Failed to load statistics');
      }
    } catch (err) {
      setError('Failed to load statistics: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num || 0);
  };

  const getStatusColor = (status) => {
    const colors = {
      'held': 'bg-blue-500',
      'released': 'bg-green-500',
      'refunded': 'bg-red-500',
      'disputed': 'bg-[#ff660e]',
      'pending': 'bg-yellow-500',
      'cancelled': 'bg-gray-500',
      'expired': 'bg-gray-400'
    };
    return colors[status] || 'bg-gray-300';
  };

  const getPeriodLabel = () => {
    const labels = {
      'today': 'Today',
      'week': 'Last 7 Days',
      'month': 'Last 30 Days',
      'year': 'Last 365 Days',
      'all': 'All Time'
    };
    return labels[period] || period;
  };

  const handleExport = () => {
    setSuccessMessage('Export feature coming soon!');
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Loading Skeleton
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 p-6">
        <div className="animate-pulse">
          {/* Header Skeleton */}
          <div className="mb-6">
            <div className="h-8 bg-gray-700 rounded w-1/4 mb-2"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
          </div>

          {/* Stats Grid Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-800 rounded-xl border border-gray-700 p-5">
                <div className="h-4 bg-gray-700 rounded w-1/2 mb-4"></div>
                <div className="h-8 bg-gray-700 rounded w-1/4 mb-2"></div>
                <div className="h-4 bg-gray-700 rounded w-3/4"></div>
              </div>
            ))}
          </div>

          {/* Charts Skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="h-96 bg-gray-800 rounded-xl border border-gray-700 p-6">
                <div className="h-6 bg-gray-700 rounded w-1/3 mb-6"></div>
                <div className="h-64 bg-gray-700 rounded"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-900 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">
              <span className="text-[#ff660e]">Escrow</span> Analytics Dashboard
            </h1>
            <p className="text-gray-400 mt-1">Comprehensive statistics and insights for escrow management</p>
          </div>
          <div className="flex flex-col md:flex-row gap-3">
            {/* Date Range Selector */}
            <div className="flex gap-2">
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg focus:ring-2 focus:ring-[#ff660e] focus:border-transparent text-white"
              >
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="year">Last 365 Days</option>
                <option value="all">All Time</option>
              </select>
              <button
                onClick={fetchStatistics}
                className="px-4 py-2 bg-gradient-to-r from-[#ff660e] to-orange-500 text-white rounded-lg hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200 font-medium flex items-center hover:scale-[1.02]"
              >
                <FiRefreshCw className="mr-2" />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-900/20 border border-green-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiCheckCircle className="text-green-400 text-xl" />
            <p className="text-green-300 font-medium">{successMessage}</p>
          </div>
          <button 
            onClick={() => setSuccessMessage('')}
            className="text-green-400 hover:text-green-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex items-center justify-between animate-fade-in backdrop-blur-sm">
          <div className="flex items-center space-x-3">
            <FiAlertTriangle className="text-red-400 text-xl" />
            <p className="text-red-300">{error}</p>
          </div>
          <button 
            onClick={() => setError('')}
            className="text-red-400 hover:text-red-300 text-xl"
          >
            ×
          </button>
        </div>
      )}

      {/* Period Info */}
      <div className="mb-6">
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-4 flex items-center justify-between hover:border-gray-600 transition-colors">
          <div className="flex items-center space-x-3">
            <FiCalendar className="text-[#ff660e]" />
            <div>
              <h3 className="font-medium text-white">Reporting Period</h3>
              <p className="text-sm text-gray-400">{getPeriodLabel()}</p>
            </div>
          </div>
          <div className="text-sm text-gray-400">
            Last updated: {new Date().toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </div>
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Total Volume */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Total Escrow Volume</p>
              <p className="text-2xl font-bold text-white mt-1 group-hover:text-[#ff660e] transition-colors">
                {formatCurrency(stats.overview?.total_amount_held || 0)}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {formatNumber(stats.overview?.total_escrows || 0)} transactions
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiDatabase className="text-blue-400 text-2xl" />
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-700">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Average:</span>
              <span className="font-medium text-gray-300">{formatCurrency(stats.overview?.avg_escrow_amount || 0)}</span>
            </div>
          </div>
        </div>

        {/* Currently Held */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Currently Held</p>
              <p className="text-2xl font-bold text-blue-400 mt-1 group-hover:text-blue-300 transition-colors">
                {formatCurrency(stats.overview?.held_amount || 0)}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {formatNumber(stats.overview?.held_escrows || 0)} active escrows
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-blue-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiClock className="text-blue-400 text-2xl" />
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-700">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Today:</span>
              <span className="font-medium text-gray-300">{formatNumber(stats.overview?.escrows_today || 0)}</span>
            </div>
          </div>
        </div>

        {/* Released Amount */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Released to Sellers</p>
              <p className="text-2xl font-bold text-green-400 mt-1 group-hover:text-green-300 transition-colors">
                {formatCurrency(stats.overview?.released_amount || 0)}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {formatNumber(stats.overview?.released_escrows || 0)} completed
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-green-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiTrendingUp className="text-green-400 text-2xl" />
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-700">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Last 7 days:</span>
              <span className="font-medium text-gray-300">{formatNumber(stats.overview?.releases_7d || 0)}</span>
            </div>
          </div>
        </div>

        {/* Refunded Amount */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-5 hover:border-gray-600 transition-colors group">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-400 text-sm font-medium">Refunded to Buyers</p>
              <p className="text-2xl font-bold text-red-400 mt-1 group-hover:text-red-300 transition-colors">
                {formatCurrency(stats.overview?.refunded_amount || 0)}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                {formatNumber(stats.overview?.refunded_escrows || 0)} refunds
              </p>
            </div>
            <div className="w-12 h-12 rounded-lg bg-red-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
              <FiTrendingDown className="text-red-400 text-2xl" />
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-700">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">Last 7 days:</span>
              <span className="font-medium text-gray-300">{formatNumber(stats.overview?.refunds_7d || 0)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Fee Revenue */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-gray-600 transition-colors">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <FiDollarSign className="mr-2 text-green-400" />
              Platform Fee Revenue
            </h3>
            <div className="text-sm text-gray-500">Total</div>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-lg bg-blue-900/30 flex items-center justify-center mr-3">
                  <FiPieChart className="text-blue-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-300">Platform Fees</p>
                  <p className="text-sm text-gray-500">Service charges</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-blue-400">
                  {formatCurrency(stats.overview?.total_platform_fees || 0)}
                </p>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-lg bg-green-900/30 flex items-center justify-center mr-3">
                  <FiCreditCard className="text-green-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-300">Payment Processing</p>
                  <p className="text-sm text-gray-500">Transaction fees</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-green-400">
                  {formatCurrency(stats.overview?.total_paystack_fees || 0)}
                </p>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-700">
              <div className="flex justify-between">
                <span className="font-semibold text-gray-300">Total Revenue</span>
                <span className="text-xl font-bold text-white">
                  {formatCurrency(
                    (stats.overview?.total_platform_fees || 0) + 
                    (stats.overview?.total_paystack_fees || 0)
                  )}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Dispute Statistics */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-gray-600 transition-colors">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <FiShield className="mr-2 text-[#ff660e]" />
              Dispute Analytics
            </h3>
            <div className="text-sm text-gray-500">{getPeriodLabel()}</div>
          </div>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-lg bg-[#ff660e]/20 flex items-center justify-center mr-3">
                  <FiActivity className="text-[#ff660e]" />
                </div>
                <div>
                  <p className="font-medium text-gray-300">Active Disputes</p>
                  <p className="text-sm text-gray-500">Requiring resolution</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-[#ff660e]">
                  {formatNumber(stats.dispute_stats?.active_disputes || 0)}
                </p>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-lg bg-green-900/30 flex items-center justify-center mr-3">
                  <FiCheckCircle className="text-green-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-300">Resolved Disputes</p>
                  <p className="text-sm text-gray-500">Successfully closed</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-green-400">
                  {formatNumber(stats.dispute_stats?.resolved_disputes || 0)}
                </p>
              </div>
            </div>
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-lg bg-red-900/30 flex items-center justify-center mr-3">
                  <FiPercent className="text-red-400" />
                </div>
                <div>
                  <p className="font-medium text-gray-300">Dispute Rate</p>
                  <p className="text-sm text-gray-500">Percentage of escrows</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-red-400">
                  {stats.dispute_stats?.dispute_rate_percentage || 0}%
                </p>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-700">
              <div className="flex justify-between">
                <span className="font-semibold text-gray-300">Total Disputed Amount</span>
                <span className="text-xl font-bold text-[#ff660e]">
                  {formatCurrency(stats.dispute_stats?.total_disputed_amount || 0)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Status Distribution */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 mb-6 hover:border-gray-600 transition-colors">
        <div className="p-6 border-b border-gray-700">
          <h3 className="text-lg font-semibold text-white flex items-center">
            <FiPieChart className="mr-2 text-purple-400" />
            Status Distribution
          </h3>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.status_distribution?.map((item) => (
              <div key={item.status} className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className={`w-4 h-4 rounded-full ${getStatusColor(item.status)} mr-3`}></div>
                    <span className="font-medium text-gray-300">
                      {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-white">{item.percentage}%</span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${getStatusColor(item.status)}`}
                    style={{ width: `${item.percentage}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-sm text-gray-500">
                  <span>{formatNumber(item.count)} escrows</span>
                  <span>{formatCurrency(item.total_amount)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* User Metrics & Type Distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* User Metrics */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-gray-600 transition-colors">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <FiUsers className="mr-2 text-indigo-400" />
              User Participation
            </h3>
            <div className="text-sm text-gray-500">{getPeriodLabel()}</div>
          </div>
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-lg bg-blue-900/30 flex items-center justify-center mr-4">
                  <FiUsers className="text-blue-400 text-xl" />
                </div>
                <div>
                  <p className="font-medium text-gray-300">Unique Buyers</p>
                  <p className="text-2xl font-bold text-blue-400 mt-1">
                    {formatNumber(stats.user_metrics?.unique_buyers || 0)}
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <div className="w-12 h-12 rounded-lg bg-green-900/30 flex items-center justify-center mr-4">
                  <FiUsers className="text-green-400 text-xl" />
                </div>
                <div>
                  <p className="font-medium text-gray-300">Unique Sellers</p>
                  <p className="text-2xl font-bold text-green-400 mt-1">
                    {formatNumber(stats.user_metrics?.unique_sellers || 0)}
                  </p>
                </div>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-700">
              <div className="text-center">
                <p className="text-sm text-gray-500 mb-2">Total Unique Users</p>
                <p className="text-3xl font-bold text-white">
                  {formatNumber(stats.user_metrics?.total_unique_users || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Type Distribution */}
        <div className="bg-gray-800 rounded-xl border border-gray-700 p-6 hover:border-gray-600 transition-colors">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <FiBarChart2 className="mr-2 text-[#ff660e]" />
              Escrow Type Breakdown
            </h3>
            <div className="text-sm text-gray-500">Distribution</div>
          </div>
          <div className="space-y-4">
            {stats.type_distribution?.map((item) => (
              <div key={item.escrow_type} className="space-y-2">
                <div className="flex justify-between items-center">
                  <div className="flex items-center">
                    <div className={`w-8 h-8 rounded-lg ${
                      item.escrow_type === 'Order-based' ? 'bg-purple-900/30' : 'bg-indigo-900/30'
                    } flex items-center justify-center mr-3`}>
                      {item.escrow_type === 'Order-based' ? (
                        <FiShoppingBag className="text-purple-400" />
                      ) : (
                        <FiMessageSquare className="text-indigo-400" />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-300">{item.escrow_type}</p>
                      <p className="text-sm text-gray-500">{item.percentage}% of total</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-white">{formatNumber(item.count)}</p>
                    <p className="text-sm text-gray-500">{formatCurrency(item.total_amount)}</p>
                  </div>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      item.escrow_type === 'Order-based' ? 'bg-purple-500' : 'bg-indigo-500'
                    }`}
                    style={{ width: `${item.percentage}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Daily Revenue Metrics */}
      {stats.revenue_metrics && stats.revenue_metrics.length > 0 && (
        <div className="bg-gray-800 rounded-xl border border-gray-700 mb-6 hover:border-gray-600 transition-colors">
          <div className="p-6 border-b border-gray-700">
            <h3 className="text-lg font-semibold text-white flex items-center">
              <FiTrendingUp className="mr-2 text-green-400" />
              Daily Volume Trends
            </h3>
          </div>
          <div className="p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Escrows
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Daily Volume
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Platform Fees
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Avg. Value
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {stats.revenue_metrics.slice(0, 7).map((day) => (
                    <tr key={day.date} className="hover:bg-gray-750 transition-colors duration-150">
                      <td className="px-4 py-3 text-sm font-medium text-white">
                        {new Date(day.date).toLocaleDateString('en-GB', {
                          weekday: 'short',
                          day: '2-digit',
                          month: 'short'
                        })}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-400">
                        {formatNumber(day.escrow_count)}
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-blue-400">
                        {formatCurrency(day.daily_volume)}
                      </td>
                      <td className="px-4 py-3 text-sm text-green-400">
                        {formatCurrency(day.daily_platform_fees)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-300">
                        {formatCurrency(day.avg_escrow_value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {stats.revenue_metrics.length > 7 && (
              <div className="text-center mt-4">
                <button className="text-sm text-[#ff660e] hover:text-orange-400 font-medium">
                  View full {stats.revenue_metrics.length} days →
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Summary Card */}
      <div className="bg-gradient-to-r from-[#ff660e] to-orange-500 rounded-xl shadow-lg p-6 text-white hover:from-[#ff5500] hover:to-orange-600 transition-all duration-200">
        <div className="flex flex-col md:flex-row md:items-center justify-between">
          <div>
            <h3 className="text-xl font-bold mb-2">Escrow Performance Summary</h3>
            <p className="opacity-90">
              {getPeriodLabel()} • {formatNumber(stats.overview?.total_escrows || 0)} transactions • 
              {formatCurrency(stats.overview?.total_amount_held || 0)} total volume
            </p>
          </div>
          <div className="mt-4 md:mt-0">
            <button 
              onClick={handleExport}
              className="px-6 py-3 bg-white text-[#ff660e] rounded-lg font-semibold hover:bg-gray-50 hover:scale-[1.02] transition-all duration-200 flex items-center"
            >
              <FiDownload className="mr-2" />
              Export Report
            </button>
          </div>
        </div>
      </div>

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default EscrowStatistics;